import Dialog from '@mui/material/Dialog';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { PrimaryButton, SecondaryButton } from './customButton';
import { ReactComponent as InfoIcon } from '../../assets/alert-icon-info.svg';
import { ReactComponent as WarningIcon } from '../../assets/alert-icon-warning.svg';
import { ReactComponent as SuccessIcon } from '../../assets/alert-icon-success.svg';
import { TextField } from "@mui/material";
import HelperText from './errorHelperText';
import { useMyContext } from '../../context/Context';
import "../../styles/alertComponent.css";

const DownloadAlert = props => {
  const { open, handleClose } = props;
  const context = useMyContext();
  const isDark = context.colorTheme === 'dark';
  return (<BaseAlert open={open} isDark={isDark}>
    <InfoIcon style={styles.icon} />
    <h1 style={{ ...styles.header, color: isDark ? '#FFFFFF' : '#00172f' }}>Preparing download</h1>
    <div style={{ ...styles.content, color: isDark ? '#E6E6E6' : '#1a426d' }}>
      Your Excel file is being prepared. This could take some time.
      Please leave this browser window open and the file will start to download when it is ready.
    </div>
    <PrimaryButton onClick={handleClose}>Close</PrimaryButton>
  </BaseAlert>);
};

// enum for message alert style
const AlertIconType = {
  none: 0,
  info: 1,
  warning: 2,
  success: 3
};

const CommonAlert = props => {
  const { open, iconType, title, message, loading,
    primaryBtnTitle, secondaryBtnTitle,
    primaryBtnAction, secondaryBtnAction
  } = props;
  const context = useMyContext();
  const isDark = context.colorTheme === 'dark';
  let AlertIcon;
  switch (iconType) {
    case AlertIconType.info:
      AlertIcon = InfoIcon;
      break;
    case AlertIconType.warning:
      AlertIcon = WarningIcon;
      break;
    case AlertIconType.success:
      AlertIcon = SuccessIcon;
      break;
    default:
      AlertIcon = () => null;
  }
  return (<BaseAlert open={open} isDark={isDark} >
    <AlertIcon style={styles.icon} />
    <h1 style={{ ...styles.header, color: isDark ? '#FFFFFF' : '#00172f' }}>{title}</h1>
    <div style={{ ...styles.content, color: isDark ? '#E6E6E6' : '#1a426d' }}>
      {message}
    </div>
    <div style={styles.btnContainer}>
      {secondaryBtnTitle ? <SecondaryButton disabled={loading} style={styles.secondaryBtn} onClick={secondaryBtnAction}>{secondaryBtnTitle}</SecondaryButton> : null}
      <PrimaryButton loading={loading} onClick={primaryBtnAction}>{primaryBtnTitle}</PrimaryButton>
    </div>
  </BaseAlert>);
};

const SavedSearchInputAlert = props => {
  const { open, handleClose, handleSubmit, errorMsg, loading } = props;
  const [searchName, setSearchName] = useState("");
  useEffect(() => {
    setSearchName("");
  }, [props.open]);
  return (<BaseAlert open={open} >
    <h1 style={{ ...styles.header, textAlign: 'left' }}>Save this search</h1>
    <div style={styles.savedSearchInputTitle}>Title</div>
    <TextField
      size="small"
      fullWidth
      style={styles.savedSearchInput}
      inputStyle={{ width: '100%' }}
      id="outlined-error-helper-text"
      label=""
      type="search"
      variant="outlined"
      value={searchName}
      inputProps={{ fullWidth: true, maxLength: 25 }}
      onChange={e => {
        const { value } = e.target;
        setSearchName(value);
      }}
      error={errorMsg}
      helperText={errorMsg ? (<HelperText text={errorMsg} />) :
        (<span style={styles.savedSearchInputHint}>
          {`${searchName.length}/25`}
        </span>)}
    />
    <div style={styles.btnContainer}>
      <SecondaryButton disabled={loading} style={styles.secondaryBtn} onClick={handleClose}>Cancel</SecondaryButton>
      <PrimaryButton loading={loading} onClick={() => handleSubmit(searchName)}>Save</PrimaryButton>
    </div>
  </BaseAlert>);
};

const BaseAlert = props => {
  const { open, children, isDark } = props;
  return <Dialog open={open}
    PaperProps={{
      style: {
        borderRadius: 12,
        backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
        color: isDark ? '#E6E6E6' : '#00172f'
      }
    }} disableEnforceFocus >
    <div style={styles.container}>
      {children}
    </div>
  </Dialog>;
};

BaseAlert.propTypes = {
  open: PropTypes.bool.isRequired,
  children: PropTypes.node,
  isDark: PropTypes.bool
};

DownloadAlert.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired
};

CommonAlert.propTypes = {
  open: PropTypes.bool.isRequired,
  iconType: PropTypes.number,
  title: PropTypes.string,
  message: PropTypes.node,
  loading: PropTypes.bool,
  primaryBtnTitle: PropTypes.string,
  secondaryBtnTitle: PropTypes.string,
  primaryBtnAction: PropTypes.func,
  secondaryBtnAction: PropTypes.func
};

SavedSearchInputAlert.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  errorMsg: PropTypes.string,
  loading: PropTypes.bool
};

const styles = {
  container: {
    padding: '24px',
    minWidth: '200px',
    maxWidth: '300px'
  },
  icon: {
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  header: {
    marginBottom: '13px',
    width: '100%',
    textAlign: 'center',
    fontSize: '20px',
    fontWeight: '500',
    fontStretch: 'normal',
    fontStyle: 'normal',
    letterSpacing: 'normal',
    color: '#00172f'
  },
  content: {
    marginBottom: '20px',
    fontSize: '16px',
    textAlign: 'center',
    fontWeight: 'normal',
    fontStretch: 'normal',
    fontStyle: 'normal',
    letterSpacing: 'normal',
    color: '#1a426d'
  },
  btnContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  secondaryBtn: {
    marginRight: '15px'
  },
  savedSearchInputTitle: {
    fontSize: '14px',
    fontWeight: '500',
    width: '300px',
    color: '#33577d',
    marginBottom: '5px'
  },
  savedSearchInput: {
    marginBottom: '20px',
    width: 300
  },
  savedSearchInputHint: {
    color: '#33577d'
  }
};

export { DownloadAlert, CommonAlert, AlertIconType, SavedSearchInputAlert };